/**
 * Flattens nested AI college data response into flat database column structure
 * Transforms grouped JSON like { basic_information: {...}, admissions_data: {...} }
 * into flat fields like { name: "...", acceptance_rate: 0.045, ... }
 */

export function flattenCollegeData(aiResponse, options = {}) {
  const { verbose = false, preserveNestedFields = false } = options;
  
  if (verbose) {
    console.log('🔧 Flattening AI college data response...');
    console.log('📝 Input keys:', Object.keys(aiResponse));
  }
  
  const flattened = {};
  
  // 1. BASIC INFORMATION mapping
  if (aiResponse.basic_information) {
    const basic = aiResponse.basic_information;
    
    // Direct mappings
    if (basic.name || basic.full_official_name) flattened.name = basic.name || basic.full_official_name;
    
    // Handle USNWR ranking
    if (basic.us_news_ranking || basic.usnwr_ranking || basic.ranking) {
      flattened.usnwr_ranking = basic.us_news_ranking || basic.usnwr_ranking || basic.ranking;
    }
    
    // Handle location - can be either a string or an object with city/state
    if (basic.location) {
      if (typeof basic.location === 'object' && basic.location.city && basic.location.state) {
        flattened.location = `${basic.location.city}, ${basic.location.state}`;
        flattened.city = basic.location.city;
        flattened.state = basic.location.state;
      } else if (typeof basic.location === 'string') {
        flattened.location = basic.location;
      }
    }
    // Also check for direct city/state if not in location object
    if (basic.city && !flattened.city) flattened.city = basic.city;
    if (basic.state && !flattened.state) flattened.state = basic.state;
    if (basic.type) flattened.type = basic.type;
    if (basic.control) flattened.control = basic.control; // public/private
    if (basic.student_enrollment || basic.undergraduate_enrollment) {
      flattened.student_enrollment = parseInt(basic.student_enrollment || basic.undergraduate_enrollment);
    }
    if (basic.campus_setting) flattened.campus_setting = basic.campus_setting;
    if (basic.religious_affiliation) flattened.religious_affiliation = basic.religious_affiliation;
    
    if (verbose && Object.keys(basic).length > 0) {
      console.log('📍 Mapped basic information:', Object.keys(basic).length, 'fields');
    }
  }
  
  // 2. ADMISSIONS DATA mapping  
  if (aiResponse.admissions_data) {
    const admissions = aiResponse.admissions_data;
    
    // Convert percentage strings to decimals
    if (admissions.overall_acceptance_rate || admissions.acceptance_rate) {
      const rate = admissions.overall_acceptance_rate || admissions.acceptance_rate;
      flattened.acceptance_rate = parseFloat(typeof rate === 'string' ? rate.replace('%', '') : rate) / (rate > 1 ? 100 : 1);
    }
    if (admissions.early_decision_acceptance_rate || admissions.ed_acceptance_rate) {
      const rate = admissions.early_decision_acceptance_rate || admissions.ed_acceptance_rate;
      flattened.ed_acceptance_rate = parseFloat(typeof rate === 'string' ? rate.replace('%', '') : rate) / (rate > 1 ? 100 : 1);
    }
    if (admissions.early_action_acceptance_rate || admissions.ea_acceptance_rate) {
      const rate = admissions.early_action_acceptance_rate || admissions.ea_acceptance_rate;
      flattened.ea_acceptance_rate = parseFloat(typeof rate === 'string' ? rate.replace('%', '') : rate) / (rate > 1 ? 100 : 1);
    }
    
    // Test scores - handle both string and object formats
    if (admissions.sat_middle_50_range) {
      if (typeof admissions.sat_middle_50_range === 'object') {
        // Handle object format like {math: "790-800", evidence_based_reading_writing: "730-780", composite: "1520-1580"}
        if (admissions.sat_middle_50_range.composite) {
          flattened.sat_range = admissions.sat_middle_50_range.composite;
        } else if (admissions.sat_middle_50_range.math && admissions.sat_middle_50_range.evidence_based_reading_writing) {
          // Calculate composite from components if not provided
          flattened.sat_range = `Math: ${admissions.sat_middle_50_range.math}, EBRW: ${admissions.sat_middle_50_range.evidence_based_reading_writing}`;
        }
        // Store individual components if they exist
        if (admissions.sat_middle_50_range.math) {
          flattened.sat_math_range = admissions.sat_middle_50_range.math;
        }
        if (admissions.sat_middle_50_range.evidence_based_reading_writing) {
          flattened.sat_reading_range = admissions.sat_middle_50_range.evidence_based_reading_writing;
        }
      } else {
        flattened.sat_range = admissions.sat_middle_50_range;
      }
    } else if (admissions.sat_range) {
      flattened.sat_range = admissions.sat_range;
    }
    
    // Handle ACT scores - can be string or object
    if (admissions.act_middle_50_range || admissions.act_range) {
      const actData = admissions.act_middle_50_range || admissions.act_range;
      if (typeof actData === 'object' && actData) {
        // Handle object format like {composite: "33-35"}
        if (actData.composite) {
          flattened.act_range = actData.composite;
        } else {
          // If no composite, try to format from individual sections
          flattened.act_range = JSON.stringify(actData);
        }
      } else {
        flattened.act_range = actData;
      }
    }
    if (admissions.gpa_middle_50_range || admissions.gpa_range) {
      flattened.gpa_range = admissions.gpa_middle_50_range || admissions.gpa_range;
    }
    
    // Test policies
    if (typeof admissions.test_optional === 'boolean') flattened.test_optional = admissions.test_optional;
    if (typeof admissions.test_blind === 'boolean') flattened.test_blind = admissions.test_blind;
    
    // Preserve full admissions data in JSONB column if it exists
    if (preserveNestedFields) {
      flattened.admissions_data = admissions;
    }
    
    if (verbose && Object.keys(admissions).length > 0) {
      console.log('📊 Mapped admissions data:', Object.keys(admissions).length, 'fields');
    }
  }
  
  // 3. FINANCIAL INFORMATION mapping
  if (aiResponse.financial_information) {
    const financial = aiResponse.financial_information;
    
    // Parse currency values
    const parseCurrency = (value) => {
      if (!value) return null;
      // Handle both string and number inputs
      const numStr = value.toString().replace(/[$,]/g, '');
      const parsed = parseInt(numStr);
      return isNaN(parsed) ? null : parsed;
    };
    
    if (financial.total_cost_of_attendance || financial.total_cost || financial.cost_of_attendance || financial.total_annual_cost) {
      flattened.total_cost = parseCurrency(financial.total_cost_of_attendance || financial.total_cost || financial.cost_of_attendance || financial.total_annual_cost);
    }
    if (financial.tuition || financial.annual_tuition || financial.tuition_and_fees) {
      flattened.tuition = parseCurrency(financial.tuition || financial.annual_tuition || financial.tuition_and_fees);
    }
    if (financial.tuition_in_state || financial.in_state_tuition || financial.resident_tuition || financial.in_state_tuition_and_fees) {
      flattened.tuition_in_state = parseCurrency(financial.tuition_in_state || financial.in_state_tuition || financial.resident_tuition || financial.in_state_tuition_and_fees);
    }
    if (financial.tuition_out_of_state || financial.out_of_state_tuition || financial.non_resident_tuition || financial.out_of_state_tuition_and_fees) {
      flattened.tuition_out_of_state = parseCurrency(financial.tuition_out_of_state || financial.out_of_state_tuition || financial.non_resident_tuition || financial.out_of_state_tuition_and_fees);
    }
    if (financial.room_and_board || financial.room_board || financial.housing_and_meals || financial.room_and_meals) {
      flattened.room_board = parseCurrency(financial.room_and_board || financial.room_board || financial.housing_and_meals || financial.room_and_meals);
    }
    if (financial.average_financial_aid_package || financial.average_aid) {
      flattened.average_aid = parseCurrency(financial.average_financial_aid_package || financial.average_aid);
    }
    
    // Financial aid percentages
    if (financial.percentage_receiving_financial_aid || financial.financial_aid_percent) {
      const percent = financial.percentage_receiving_financial_aid || financial.financial_aid_percent;
      flattened.financial_aid_percent = parseFloat(typeof percent === 'string' ? percent.replace('%', '') : percent) / (percent > 1 ? 100 : 1);
    }
    
    if (financial.merit_scholarship_availability) {
      flattened.merit_scholarships = financial.merit_scholarship_availability;
    }
    
    if (verbose && Object.keys(financial).length > 0) {
      console.log('💰 Mapped financial information:', Object.keys(financial).length, 'fields');
    }
  }
  
  // 4. ACADEMIC PROGRAMS mapping
  if (aiResponse.academic_programs) {
    const academic = aiResponse.academic_programs;
    
    if (academic.most_popular_majors || academic.popular_majors) {
      const majors = academic.most_popular_majors || academic.popular_majors;
      flattened.popular_majors = Array.isArray(majors) ? majors.join(', ') : majors;
    }
    if (academic.special_programs) {
      flattened.special_programs = Array.isArray(academic.special_programs) 
        ? academic.special_programs.join(', ') 
        : academic.special_programs;
    }
    if (academic.study_abroad_participation_rate || academic.study_abroad_rate) {
      const rate = academic.study_abroad_participation_rate || academic.study_abroad_rate;
      flattened.study_abroad_rate = parseFloat(typeof rate === 'string' ? rate.replace('%', '') : rate) / (rate > 1 ? 100 : 1);
    }
    if (academic.student_faculty_ratio) {
      flattened.student_faculty_ratio = academic.student_faculty_ratio;
    }
    if (academic.class_sizes) {
      flattened.class_sizes = academic.class_sizes;
    }
    
    // Preserve full academic data in JSONB column if it exists
    if (preserveNestedFields) {
      flattened.academic_programs = academic;
    }
    
    if (verbose && Object.keys(academic).length > 0) {
      console.log('🎓 Mapped academic programs:', Object.keys(academic).length, 'fields');
    }
  }
  
  // 5. DEADLINES mapping - handle both "deadlines" and "deadlines_fall_2026" keys
  const deadlines = aiResponse.deadlines || aiResponse.deadlines_fall_2026 || aiResponse.deadlines_fall_2025;
  if (deadlines) {
    
    // Helper to parse dates
    const parseDeadline = (dateStr) => {
      if (!dateStr) return null;
      // Skip "Not applicable" or similar non-date values
      if (typeof dateStr === 'string' && (
        dateStr.toLowerCase().includes('not applicable') || 
        dateStr.toLowerCase().includes('n/a') ||
        dateStr.toLowerCase().includes('none')
      )) {
        return null;
      }
      try {
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? null : date.toISOString().split('T')[0]; // YYYY-MM-DD format
      } catch (e) {
        return null; // Return null if parsing fails
      }
    };
    
    if (deadlines.early_decision_deadline || deadlines.ed_deadline) {
      flattened.ed_deadline = parseDeadline(deadlines.early_decision_deadline || deadlines.ed_deadline);
    }
    if (deadlines.early_decision_ii_deadline || deadlines.ed2_deadline) {
      flattened.ed2_deadline = parseDeadline(deadlines.early_decision_ii_deadline || deadlines.ed2_deadline);
    }
    if (deadlines.early_action_deadline || deadlines.ea_deadline) {
      flattened.ea_deadline = parseDeadline(deadlines.early_action_deadline || deadlines.ea_deadline);
    }
    if (deadlines.regular_decision_deadline || deadlines.rd_deadline) {
      flattened.rd_deadline = parseDeadline(deadlines.regular_decision_deadline || deadlines.rd_deadline);
    }
    if (deadlines.financial_aid_deadline || deadlines.finaid_deadline) {
      flattened.finaid_deadline = parseDeadline(deadlines.financial_aid_deadline || deadlines.finaid_deadline);
    }
    if (deadlines.css_profile_deadline || deadlines.css_deadline) {
      flattened.css_deadline = parseDeadline(deadlines.css_profile_deadline || deadlines.css_deadline);
    }
    if (deadlines.merit_scholarship_deadlines || deadlines.merit_deadline) {
      flattened.merit_deadline = parseDeadline(deadlines.merit_scholarship_deadlines || deadlines.merit_deadline);
    }
    
    if (verbose && Object.keys(deadlines).length > 0) {
      console.log('📅 Mapped deadlines:', Object.keys(deadlines).length, 'fields');
    }
  }
  
  // 6. INSTITUTIONAL VALUES mapping
  if (aiResponse.institutional_values) {
    const values = aiResponse.institutional_values;
    
    if (values.core_values_and_mission || values.core_values) {
      flattened.core_values = values.core_values_and_mission || values.core_values;
    }
    if (values.what_they_look_for_in_students || values.student_qualities) {
      flattened.student_qualities = values.what_they_look_for_in_students || values.student_qualities;
    }
    if (values.institutional_priorities) {
      flattened.institutional_priorities = values.institutional_priorities;
    }
    if (values.campus_culture_highlights || values.campus_culture) {
      flattened.campus_culture = values.campus_culture_highlights || values.campus_culture;
    }
    
    if (verbose && Object.keys(values).length > 0) {
      console.log('🎯 Mapped institutional values:', Object.keys(values).length, 'fields');
    }
  }
  
  // 7. ESSAY REQUIREMENTS mapping
  if (aiResponse.essay_requirements) {
    const essays = aiResponse.essay_requirements;
    
    if (essays.number_of_supplemental_essays || essays.supplement_count) {
      flattened.supplement_count = parseInt(essays.number_of_supplemental_essays || essays.supplement_count);
    }
    if (essays.essay_prompts_and_word_limits || essays.essay_prompts) {
      const prompts = essays.essay_prompts_and_word_limits || essays.essay_prompts;
      // Handle array of prompt objects
      if (Array.isArray(prompts)) {
        flattened.essay_prompts = prompts.map((prompt, index) => {
          if (typeof prompt === 'object' && prompt.prompt) {
            return `${index + 1}. ${prompt.prompt}${prompt.word_limit ? ` (${prompt.word_limit} words)` : ''}`;
          }
          return prompt;
        }).join('\n\n');
      } else {
        flattened.essay_prompts = prompts;
      }
    }
    if (essays.why_us_essay_specifics || essays.why_essay) {
      flattened.why_essay = essays.why_us_essay_specifics || essays.why_essay;
    }
    
    if (verbose && Object.keys(essays).length > 0) {
      console.log('📝 Mapped essay requirements:', Object.keys(essays).length, 'fields');
    }
  }
  
  // 8. Handle any direct flat fields that are already properly named
  const directFields = ['rankings', 'usnwr_ranking', 'liberal_arts_ranking', 'engineering_ranking'];
  directFields.forEach(field => {
    if (aiResponse[field] !== undefined) {
      flattened[field] = aiResponse[field];
    }
  });
  
  if (verbose) {
    console.log('✅ Flattening complete. Output fields:', Object.keys(flattened).length);
    console.log('📋 Flattened keys:', Object.keys(flattened).join(', '));
  }
  
  return flattened;
}

/**
 * Helper function to identify which columns are missing from the database
 * and might need to be added for a complete mapping
 */
export function identifyMissingColumns(aiResponse, existingColumns) {
  const flatData = flattenCollegeData(aiResponse, { verbose: false });
  const missingColumns = [];
  
  Object.keys(flatData).forEach(column => {
    if (!existingColumns.includes(column)) {
      missingColumns.push(column);
    }
  });
  
  return {
    missingColumns,
    totalMapped: Object.keys(flatData).length,
    successfullyMapped: Object.keys(flatData).length - missingColumns.length
  };
}